import { createSelector } from "@reduxjs/toolkit";

import USER_PERMISSIONS from "../../dto/User/UserPermissions";
import { ADDITIONAL_JOBS_TYPE_ENUM } from "../../dto/Settings/AdditionalJobDto";
import { RootState } from "../../configureStore";
import { TransportationTypeType } from "../../dto/Settings/TransportationTypeDto";
import { UserInfoModel } from "../../viewModels/User/UserInfoModel";
import { DevRoleOverrideUserType } from "./reducer/types";

const devRoleNames: Record<DevRoleOverrideUserType, string> = {
  ADMINISTRATOR: "Администратор",
  MANAGER: "Менеджер",
  LOGISTICIAN: "Логист",
  MANAGER_LOGISTICIAN: "Менеджер-логист",
  ACCOUNTANT: "Бухгалтер",
};

const selectGlobal = (state: RootState) => state.global;

const makeSelectRegularBitrixCompanies = () => createSelector(selectGlobal, (globalState) => globalState.regularBitrixCompanies);
const makeSelectClients = () => createSelector(selectGlobal, (globalState) => globalState.clients);
const makeSelectCarriers = () => createSelector(selectGlobal, (globalState) => globalState.carriers);

const makeSelectLoading = () => createSelector(selectGlobal, (globalState) => globalState.loading);
const makeSelectUserDataLoading = () => createSelector(selectGlobal, (globalState) => globalState.userDataLoading);

const makeSelectInitialLoadingError = () => createSelector(selectGlobal, (globalState) => globalState.initialLoadingError);

const makeSelectAuthenticatedUserInfo = () => createSelector(selectGlobal, (globalState) => globalState.userInfo);

const makeSelectUserInfo = () =>
  createSelector(selectGlobal, (globalState): UserInfoModel => {
    const userInfo = globalState.userInfo;
    const override = globalState.devRoleOverride;

    if (!userInfo || userInfo.role.userType !== "ADMINISTRATOR" || !override) {
      return userInfo;
    }

    return {
      ...userInfo,
      role: {
        ...userInfo.role,
        name: devRoleNames[override.userType],
        userType: override.userType,
        isBoss: override.isBoss,
      },
    };
  });

const makeSelectDevRoleOverride = () => createSelector(selectGlobal, (globalState) => globalState.devRoleOverride);

const makeSelectIsShowCarrierInfo = () =>
  createSelector(
    selectGlobal,
    (globalState) =>
      globalState.userInfo &&
      globalState.userInfo.company.types.includes("FORWARDER") &&
      globalState.userInfo.role.permissions.includes(USER_PERMISSIONS.DICTIONARY_ROTE_VIEW),
  );

const makeSelectIsAuthModalVisible = () => createSelector(selectGlobal, (globalState) => globalState.isAuthModalVisible);

const makeSelectIsContactModalVisible = () => createSelector(selectGlobal, (globalState) => globalState.isContactModalVisible);

const makeSelectIsContactModalLoading = () => createSelector(selectGlobal, (globalState) => globalState.isContactModalLoading);

const makeSelectTransportCharacteristics = () => createSelector(selectGlobal, (globalState) => globalState.transportCharacteristics);
const makeSelectTransportCharacteristicById = (id: number) =>
  createSelector(makeSelectTransportCharacteristics(), (transportCharacteristics) => transportCharacteristics.find((x) => x.id === id)!);
const makeSelectTransportCharacteristicsOptions = () =>
  createSelector(makeSelectTransportCharacteristics(), (transportCharacteristics) =>
    transportCharacteristics.map((x) => ({ label: x.shortName, value: x.id })),
  );
const makeSelectContainerTypeOptions = () =>
  createSelector(makeSelectTransportCharacteristics(), (transportCharacteristics) =>
    transportCharacteristics.find((x) => x.type === "LIST")?.possibleValues?.map((x) => ({ label: x.value, value: x.id })),
  );

const makeSelectIsContainerTransportation = (id: number) =>
  createSelector(
    makeSelectTransportCharacteristics(),
    (transportCharacteristics) => transportCharacteristics.find((x) => x.id === id)?.loadType === "FCL",
  );

const makeSelectTransportationTypes = () => createSelector(selectGlobal, (globalState) => globalState.transportationTypes);
const makeSelectTransportationTypeTypeById = (id: number) =>
  createSelector(makeSelectTransportationTypes(), (transportationTypes) => transportationTypes.find((x) => x.id === id)?.type);
const makeSelectTransportationTypesByIds = (ids: Array<number>) =>
  createSelector(makeSelectTransportationTypes(), (transportationTypes) => transportationTypes.filter((x) => ids.indexOf(x.id) !== -1));
const makeSelectTransportationTypeNameById = (id: number) =>
  createSelector(makeSelectTransportationTypes(), (transportationTypes) => transportationTypes.find((x) => x.id === id)?.name);
const makeSelectTransportationTypesOptions = () =>
  createSelector(makeSelectTransportationTypes(), (transportationTypes) =>
    transportationTypes.map((x) => ({ label: x.name, value: x.id })),
  );

const makeSelectAdditionalJobs = () => createSelector(selectGlobal, (globalState) => globalState.additionalJobs);
const makeSelectContainerIssuingAdditionalJob = () =>
  createSelector(
    makeSelectAdditionalJobs(),
    (additionalJobs) => additionalJobs.find((x) => x.type === ADDITIONAL_JOBS_TYPE_ENUM.CONTAINER_ISSUING)!,
  );

const makeSelectDocumentTypes = () => createSelector(selectGlobal, (globalState) => globalState.documentTypes);
const makeSelectDocumentTypeById = (id: number) => createSelector(makeSelectDocumentTypes(), (types) => types.find((x) => x.id === id)!);
const makeSelectApplicationStatuses = () => createSelector(selectGlobal, (globalState) => globalState.applicationStatuses);
const makeSelectApplicationStatusById = (id: number) =>
  createSelector(makeSelectApplicationStatuses(), (statuses) => statuses.find((x) => x.id === id));

const makeSelectRoleUserActions = () => createSelector(selectGlobal, (globalState) => globalState.roleUserActions);
const makeSelectRoleUserActionsByIds = (ids: Array<number>) =>
  createSelector(makeSelectRoleUserActions(), (actions) => actions.filter((x) => ids.indexOf(x.id) !== -1));

const makeSelectRoleUserActionSettings = () => createSelector(selectGlobal, (globalState) => globalState.roleUserActionSettings);

const makeSelectCountries = () => createSelector(selectGlobal, (globalState) => globalState.countries);
const makeSelectCountryById = (id: number) => createSelector(makeSelectCountries(), (countries) => countries.find((x) => x.id === id));

const makeSelectLocationTypes = () => createSelector(selectGlobal, (globalState) => globalState.locationTypes);
const makeSelectLocationTypeById = (id: number) =>
  createSelector(makeSelectLocationTypes(), (locationTypes) => locationTypes.find((x) => x.id === id));

const makeSelectCompanyTypes = () => createSelector(selectGlobal, (globalState) => globalState.companyTypes);

const makeSelectMessageConfigShow = () => createSelector(selectGlobal, (globalState) => globalState.messageConfigShow);

const makeSelectWaypointLocations = () => createSelector(selectGlobal, (globalState) => globalState.waypointLocations);
const makeSelectBorderCrossingLocations = () => createSelector(selectGlobal, (globalState) => globalState.borderCrossingLocations);
const makeSelectBorderCrossingPairs = () => createSelector(selectGlobal, (globalState) => globalState.borderCrossingPairs);

const makeSelectUserTypes = () => createSelector(selectGlobal, (globalState) => globalState.userTypes);

const makeSelectApplicationServices = () => createSelector(selectGlobal, (globalState) => globalState.applicationServices);

const makeSelectHost = () => createSelector(selectGlobal, (globalState) => globalState.host);

const makeSelectPreviewHost = () =>
  createSelector(selectGlobal, (globalState) =>
    globalState.userInfo?.role.userType === "ADMINISTRATOR" && globalState.devRoleOverride
      ? globalState.devRoleOverride.host
      : globalState.host,
  );
const makeSelectBitrixOptions = () => createSelector(selectGlobal, (globalState) => globalState.bitrixOptions);

const makeSelectDisplayScope = () =>
  createSelector(selectGlobal, (globalState) =>
    globalState.userInfo?.role.userType === "ADMINISTRATOR" && globalState.devRoleOverride
      ? globalState.devRoleOverride.displayScope
      : globalState.displayScope,
  );

const makeSelectBitrixCarriersLoading = () => createSelector(selectGlobal, (globalState) => globalState.bitrixCarriersLoading);
const makeSelectBitrixCarriers = () => createSelector(selectGlobal, (globalState) => globalState.bitrixCarriers);
const makeSelectBitrixCompanyDetailsById = (id: number) =>
  createSelector(selectGlobal, (globalState) => globalState.bitrixCompanyDetailsById[id]);
const makeSelectBitrixCompanyDetailsLoadingById = (id: number) =>
  createSelector(selectGlobal, (globalState) => !!globalState.bitrixCompanyDetailsLoadingById[id]);
const makeSelectBitrixCompanyDetailsErrorById = (id: number) =>
  createSelector(selectGlobal, (globalState) => !!globalState.bitrixCompanyDetailsErrorById[id]);

const makeSelectIncotermsTypes = () => createSelector(selectGlobal, (globalState) => globalState.incotermsTypes);
const makeSelectSettlementAccounts = () => createSelector(selectGlobal, (globalState) => globalState.settlementAccounts);

const makeSelectLanguage = () => createSelector(selectGlobal, (globalState) => globalState.language);

const makeSelectCurrencies = () => createSelector(selectGlobal, (globalState) => globalState.currencies);
const makeSelectCurrencyById = (id: number) =>
  createSelector(makeSelectCurrencies(), (currencies) => currencies?.find((x) => x.id === id)!);
const makeSelectBaseCompanyCurrency = () =>
  createSelector(selectGlobal, (globalState) => globalState.currencies?.find((x) => x.id === globalState.userInfo.company.currencyId)!);

const makeSelectGlobalCurrencies = () => createSelector(selectGlobal, (globalState) => globalState.globalCurrencies);
const makeSelectGlobalCurrencyById = (id: number) =>
  createSelector(makeSelectGlobalCurrencies(), (currencies) => currencies?.find((x) => x.id === id)!);

const makeSelectManagers = () => createSelector(selectGlobal, (globalState) => globalState.managers);
const makeSelectLogisticians = () => createSelector(selectGlobal, (globalState) => globalState.logisticians);

const makeSelectNomenclatures = () => createSelector(selectGlobal, (globalState) => globalState.nomenclatures);

const makeSelectExpenseCategoryServices = () => createSelector(selectGlobal, (globalState) => globalState.expenseCategoryServices);
const makeSelectPaymentKinds = () => createSelector(selectGlobal, (globalState) => globalState.paymentKinds);
const makeSelectPaymentTypes = () => createSelector(selectGlobal, (globalState) => globalState.paymentTypes);

const makeSelectCompanySettings = () => createSelector(selectGlobal, (globalState) => globalState.userInfo.company.settings);
const makeSelectDocumentLanguages = () =>
  createSelector(selectGlobal, (globalState) => globalState.userInfo.company.settings.documentLanguages);
const makeSelectClosingDocumentTypes = () =>
  createSelector(selectGlobal, (globalState) => globalState.userInfo.company.settings.closingDocumentTypes);

const makeSelectDealsBasicInfo = () => createSelector(selectGlobal, (globalState) => globalState.dealsBasicInfo);

const makeSelectRegions = () => createSelector(selectGlobal, (globalState) => globalState.regions);

const makeSelectUsers = () => createSelector(selectGlobal, (globalState) => globalState.users);

const makeSelectPendingFillDeals = () => createSelector(selectGlobal, (globalState) => globalState.pendingFillDeals);

const makeSelectDealNotitficationsToUs = () => createSelector(selectGlobal, (globalState) => globalState.dealNotitficationsToUs);
const makeSelectDealNotitficationsByUs = () => createSelector(selectGlobal, (globalState) => globalState.dealNotitficationsByUs);

const makeSelectDealNotificationsLoadingData = () =>
  createSelector(selectGlobal, (globalState) => globalState.dealNotificationsLoadingData);
const makeSelectTransportationSchemes = () => createSelector(selectGlobal, (globalState) => globalState.transportationSchemes);

const makeSelectVehicleTypes = () => createSelector(selectGlobal, (globalState) => globalState.vehicleTypes);
const makeSelectVehicleTypesByTransportationType = (transportationType: TransportationTypeType) =>
  createSelector(selectGlobal, (globalState) => globalState.vehicleTypes.filter((x) => x.transportationTypes.includes(transportationType)));

const makeSelectLeadStatuses = () => createSelector(selectGlobal, (globalState) => globalState.leadStatuses);

const makeSelectDangerousGoodsClasses = () => createSelector(selectGlobal, (globalState) => globalState.dangerousGoodsClasses);

const makeSelectTransportationStages = () => createSelector(selectGlobal, (globalState) => globalState.transportationStages);

export {
  makeSelectRegularBitrixCompanies,
  makeSelectClients,
  makeSelectCarriers,
  makeSelectLoading,
  makeSelectUserInfo,
  makeSelectAuthenticatedUserInfo,
  makeSelectDevRoleOverride,
  makeSelectIsAuthModalVisible,
  makeSelectIsContactModalVisible,
  makeSelectIsContactModalLoading,
  makeSelectCurrencies,
  makeSelectCurrencyById,
  makeSelectTransportCharacteristics,
  makeSelectTransportCharacteristicsOptions,
  makeSelectTransportationTypes,
  makeSelectAdditionalJobs,
  makeSelectDocumentTypes,
  makeSelectDocumentTypeById,
  makeSelectApplicationStatuses,
  makeSelectApplicationStatusById,
  makeSelectInitialLoadingError,
  makeSelectTransportCharacteristicById,
  makeSelectTransportationTypesByIds,
  makeSelectTransportationTypeTypeById,
  makeSelectTransportationTypeNameById,
  makeSelectTransportationTypesOptions,
  makeSelectMessageConfigShow,
  makeSelectRoleUserActions,
  makeSelectRoleUserActionsByIds,
  makeSelectRoleUserActionSettings,
  makeSelectContainerTypeOptions,
  makeSelectCountries,
  makeSelectCountryById,
  makeSelectLocationTypes,
  makeSelectLocationTypeById,
  makeSelectIsContainerTransportation,
  makeSelectCompanyTypes,
  makeSelectIsShowCarrierInfo,
  makeSelectContainerIssuingAdditionalJob,
  makeSelectWaypointLocations,
  makeSelectUserTypes,
  makeSelectBorderCrossingLocations,
  makeSelectBorderCrossingPairs,
  makeSelectApplicationServices,
  makeSelectHost,
  makeSelectPreviewHost,
  makeSelectBitrixOptions,
  makeSelectBitrixCarriersLoading,
  makeSelectBitrixCarriers,
  makeSelectBitrixCompanyDetailsById,
  makeSelectBitrixCompanyDetailsLoadingById,
  makeSelectBitrixCompanyDetailsErrorById,
  makeSelectIncotermsTypes,
  makeSelectSettlementAccounts,
  makeSelectLanguage,
  makeSelectGlobalCurrencies,
  makeSelectGlobalCurrencyById,
  makeSelectBaseCompanyCurrency,
  makeSelectManagers,
  makeSelectLogisticians,
  makeSelectNomenclatures,
  makeSelectDisplayScope,
  makeSelectExpenseCategoryServices,
  makeSelectPaymentKinds,
  makeSelectPaymentTypes,
  makeSelectCompanySettings,
  makeSelectDocumentLanguages,
  makeSelectClosingDocumentTypes,
  makeSelectDealsBasicInfo,
  makeSelectUserDataLoading,
  makeSelectRegions,
  makeSelectUsers,
  makeSelectPendingFillDeals,
  makeSelectDealNotitficationsToUs,
  makeSelectDealNotitficationsByUs,
  makeSelectDealNotificationsLoadingData,
  makeSelectLeadStatuses,
  makeSelectDangerousGoodsClasses,
  makeSelectTransportationStages,
  makeSelectTransportationSchemes,
  makeSelectVehicleTypes,
  makeSelectVehicleTypesByTransportationType,
};
